import { useRef, useEffect } from 'react';
import type { SubscriptionPlan } from '../../types/signup';
import '../../styles/signup.css';

interface PaymentPreviewProps {
  cardHolder: string;
  cardNumber: string;
  expiryMonth: string;
  expiryYear: string;
  selectedPlan: SubscriptionPlan | null;
}

const getCardBrandIcon = (number: string): string => {
  const stripped = number.replace(/\s/g, '');
  if (stripped.startsWith('4')) return 'visa';
  if (/^5[1-5]/.test(stripped)) return 'mastercard';
  if (/^3[47]/.test(stripped)) return 'amex';
  return 'credit_card';
};

const formatCardDisplay = (number: string): string => {
  const stripped = number.replace(/\s/g, '');
  const padded = stripped.padEnd(16, '•');
  const groups = [];
  for (let i = 0; i < 16; i += 4) {
    groups.push(padded.slice(i, i + 4));
  }
  return groups.join('  ');
};

export const PaymentPreview = ({
  cardHolder,
  cardNumber,
  expiryMonth,
  expiryYear,
  selectedPlan,
}: PaymentPreviewProps) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const displayHolder = cardHolder.trim() || 'TU NOMBRE';
  const displayExpiry =
    expiryMonth && expiryYear
      ? `${expiryMonth.padStart(2, '0')} / ${expiryYear.slice(-2)}`
      : 'MM / AA';

  const brandIcon = getCardBrandIcon(cardNumber);
  const isVisa = brandIcon === 'visa';
  const isMastercard = brandIcon === 'mastercard';

  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMouseMove = (e: MouseEvent) => {
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const rotateX = ((y - rect.height / 2) / rect.height) * -12;
      const rotateY = ((x - rect.width / 2) / rect.width) * 12;
      requestAnimationFrame(() => {
        card.style.setProperty(
          'transform',
          `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
        );
        card.style.setProperty('--mouse-x', `${(x / rect.width) * 100}%`);
        card.style.setProperty('--mouse-y', `${(y / rect.height) * 100}%`);
      });
    };

    const handleMouseLeave = () => {
      requestAnimationFrame(() => {
        card.style.setProperty(
          'transform',
          'perspective(1000px) rotateX(0deg) rotateY(0deg)'
        );
      });
    };

    card.addEventListener('mousemove', handleMouseMove);
    card.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      card.removeEventListener('mousemove', handleMouseMove);
      card.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <div className="flex flex-col items-center gap-8 w-full max-w-[520px]">
      {/* Credit Card Visual */}
      <div className="card-3d-wrapper w-full">
        <div
          ref={cardRef}
          className="digital-card-frost card-3d w-full aspect-[1.586/1] rounded-2xl p-8 flex flex-col justify-between transition-smooth hover-lift"
          style={
            {
              '--mouse-x': '50%',
              '--mouse-y': '50%',
            } as React.CSSProperties
          }
        >
          <div className="refracted-border rounded-2xl"></div>

          {/* Top: chip + brand */}
          <div className="flex justify-between items-start relative z-10">
            {/* EMV Chip */}
            <div className="w-12 h-9 rounded-md bg-gradient-to-br from-yellow-300 to-yellow-500 shadow-md flex items-center justify-center">
              <div className="grid grid-cols-3 gap-[2px] w-8 h-6">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="bg-yellow-600/40 rounded-[1px]" />
                ))}
              </div>
            </div>

            {/* Card brand */}
            <div className="flex items-center gap-1">
              {isVisa && (
                <span className="text-[#101822] font-extrabold text-2xl italic tracking-tight">
                  VISA
                </span>
              )}
              {isMastercard && (
                <div className="flex -space-x-3">
                  <div className="w-8 h-8 rounded-full bg-red-500 opacity-90"></div>
                  <div className="w-8 h-8 rounded-full bg-orange-400 opacity-90"></div>
                </div>
              )}
              {!isVisa && !isMastercard && (
                <span className="material-icons-round text-[#101822]/40 text-3xl">
                  credit_card
                </span>
              )}
            </div>
          </div>

          {/* Card number */}
          <div className="relative z-10 mt-4">
            <p className="font-mono text-xl md:text-2xl font-bold text-[#101822] tracking-[0.2em] leading-none">
              {formatCardDisplay(cardNumber)}
            </p>
          </div>

          {/* Bottom: holder + expiry */}
          <div className="flex justify-between items-end border-t border-[#101822]/5 pt-4 relative z-10">
            <div>
              <p className="text-[9px] text-[#101822]/30 font-bold uppercase tracking-widest mb-1">
                Titular
              </p>
              <p className="text-sm font-bold text-[#101822] uppercase tracking-wider truncate max-w-[200px]">
                {displayHolder}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[9px] text-[#101822]/30 font-bold uppercase tracking-widest mb-1">
                Expira
              </p>
              <p className="text-sm font-bold text-[#101822] font-mono">{displayExpiry}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Info */}
      {selectedPlan && (
        <div className="w-full digital-card-frost rounded-2xl p-6 flex flex-col gap-3">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold text-[#101822]/40 uppercase tracking-widest mb-1">
                Plan seleccionado
              </p>
              <h3 className="text-xl font-extrabold text-[#101822]">{selectedPlan.name}</h3>
              <p className="text-sm text-[#101822]/50 mt-1 leading-snug">
                {selectedPlan.description}
              </p>
            </div>
            <div className="text-right shrink-0 ml-4">
              <p className="text-3xl font-extrabold text-primary">
                {selectedPlan.currencyCode}&nbsp;
                {selectedPlan.paymentLink.amount.toLocaleString('es', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </p>
              <p className="text-xs text-[#101822]/40 font-medium">
                /{' '}
                {selectedPlan.billingCycleType === 'monthly'
                  ? 'mes'
                  : selectedPlan.billingCycleType === 'annual'
                  ? 'año'
                  : selectedPlan.billingCycleType}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-[#101822]/5">
            <div className="w-2 h-2 rounded-full bg-primary pulse-glow"></div>
            <span className="text-xs text-primary font-bold uppercase tracking-wider">
              Acceso completo incluido
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
